import { useState, useEffect } from 'react';
import { Camera, Trash2, Plus, Upload, X, Download, Search, Edit } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSignedUrls } from '../../hooks/useSignedUrl';
import { fetchStoredBytes } from '../../utils/fileUrls';

export function TruckPhotosView() {
  const toast = useToast();
  const { profile } = useAuth();
  const [trucks, setTrucks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTruckId, setSelectedTruckId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [truckSearchQuery, setTruckSearchQuery] = useState('');
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [currentTruckForPhotos, setCurrentTruckForPhotos] = useState<any>(null);
  const [imageLoading, setImageLoading] = useState(false);
  // Photos are in a private bucket: sign the open lightbox's photos in one request.
  const { urls: signedPhotoUrls, loading: signedPhotosLoading } = useSignedUrls(showPhotoModal ? selectedPhotos : []);
  
  // Check if user can edit (Safety and Safety Manager can edit, Dispatchers can only download)
  const canEdit = profile?.role === 'Safety' || profile?.role === 'Safety Manager' || profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin';

  useEffect(() => {
    loadTrucksWithPhotos();
  }, []);

  // Preload ALL images when modal opens for instant navigation
  useEffect(() => {
    if (showPhotoModal && selectedPhotos.length > 0 && !signedPhotosLoading) {
      setImageLoading(true);
      
      // Preload all images in the array (signed URLs)
      const imagePromises = selectedPhotos.map((url) => {
        const signed = signedPhotoUrls.get(url.trim());
        return new Promise((resolve, reject) => {
          if (!signed) { reject(new Error('unsigned')); return; }
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = signed;
        });
      });

      // Wait for current image to load before hiding spinner
      const currentSigned = signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim());
      if (currentSigned) {
        const currentImg = new Image();
        currentImg.onload = () => setImageLoading(false);
        currentImg.onerror = () => setImageLoading(false);
        currentImg.src = currentSigned;
      } else {
        setImageLoading(false);
      }

      // Preload rest in background (don't wait)
      Promise.all(imagePromises).catch(() => {});
    }
  }, [showPhotoModal, selectedPhotos, signedPhotoUrls, signedPhotosLoading]);

  // Set loading state when changing images
  useEffect(() => {
    if (showPhotoModal) {
      setImageLoading(true);
    }
  }, [currentPhotoIndex, showPhotoModal]);

  async function loadTrucksWithPhotos() {
    try {
      setLoading(true);
      
      // Load all trucks
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('id, unit_number, vin')
        .order('unit_number');

      if (trucksError) throw trucksError;

      // Load all truck photos from truck_load_photos table
      const { data: photosData, error: photosError } = await supabase
        .from('truck_load_photos')
        .select('*')
        .order('created_at', { ascending: false });

      if (photosError) throw photosError;

      // Group photos by truck number
      const photosMap = new Map<string, any[]>();
      (photosData || []).forEach(photo => {
        const existing = photosMap.get(photo.truck_number) || [];
        photosMap.set(photo.truck_number, [...existing, photo]);
      });

      // Combine trucks with their photos
      const trucksWithPhotos = (trucksData || []).map(truck => ({
        ...truck,
        photos: photosMap.get(truck.unit_number) || []
      }));

      setTrucks(trucksWithPhotos);
    } catch (error) {
      console.error('Error loading trucks:', error);
      toast.error('Failed to load truck photos');
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteSinglePhoto = async (photoUrl: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      // Delete the photo record from truck_load_photos
      const { error } = await supabase
        .from('truck_load_photos')
        .delete()
        .eq('photo_url', photoUrl);

      if (error) throw error;

      // Delete from storage
      const urlPath = photoUrl.split('/truck-photos/')[1];
      if (urlPath) {
        await supabase.storage
          .from('company-documents')
          .remove([`truck-photos/${urlPath}`]);
      }

      // Update local state
      const updatedPhotos = selectedPhotos.filter(url => url !== photoUrl);
      setSelectedPhotos(updatedPhotos);
      
      if (updatedPhotos.length === 0) {
        setShowPhotoModal(false);
      } else if (currentPhotoIndex >= updatedPhotos.length) {
        setCurrentPhotoIndex(updatedPhotos.length - 1);
      }

      toast.success('Photo deleted successfully');
      loadTrucksWithPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteAllTruckPhotos = async (truckPhotos: any[]) => {
    if (!truckPhotos || truckPhotos.length === 0) return;
    
    if (!confirm(`Are you sure you want to delete all photos for this truck? This will remove ${truckPhotos.length} photo(s).`)) return;

    try {
      // Delete all photo records for this truck
      for (const photoRecord of truckPhotos) {
        const { error } = await supabase
          .from('truck_load_photos')
          .delete()
          .eq('id', photoRecord.id);

        if (error) throw error;
      }

      toast.success('All truck photos deleted successfully');
      loadTrucksWithPhotos();
    } catch (error) {
      console.error('Error deleting truck photos:', error);
      toast.error('Failed to delete truck photos');
    }
  };

  const openPhotoModal = (photos: string[], truck: any) => {
    setSelectedPhotos(photos);
    setCurrentPhotoIndex(0);
    setCurrentTruckForPhotos(truck);
    setShowPhotoModal(true);
  };

  const getAllPhotosForTruck = (truckPhotos: any[]) => {
    // Each photo record has one photo_url
    return truckPhotos.map(photo => photo.photo_url).filter(Boolean);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUploadPhotos = async () => {
    if (!selectedTruckId) {
      toast.error('Please select a truck');
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const selectedTruck = trucks.find(t => t.id === selectedTruckId);
      if (!selectedTruck) throw new Error('Truck not found');

      const uploadedUrls: string[] = [];

      // Upload each file to company-documents storage
      for (const file of selectedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${selectedTruck.unit_number}_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `truck-photos/${selectedTruck.unit_number}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Save each photo to truck_load_photos table
      const photoInserts = uploadedUrls.map(url => ({
        truck_number: selectedTruck.unit_number,
        photo_url: url,
        uploaded_by: user?.email || 'unknown'
      }));

      const { error: dbError } = await supabase
        .from('truck_load_photos')
        .insert(photoInserts);

      if (dbError) throw dbError;

      toast.success(`${selectedFiles.length} photo(s) uploaded successfully!`);
      setShowUploadModal(false);
      setSelectedTruckId('');
      setSelectedFiles([]);
      loadTrucksWithPhotos();
    } catch (error: any) {
      console.error('Error uploading photos:', error);
      toast.error(`Failed to upload photos: ${error.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPhotos = async (truckPhotos: any[]) => {
    const allPhotos = getAllPhotosForTruck(truckPhotos);
    if (allPhotos.length === 0) {
      toast.error('No photos to download');
      return;
    }

    let downloaded = 0;
    for (let i = 0; i < allPhotos.length; i++) {
      try {
        const blob = await fetchStoredBytes(allPhotos[i]);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `photo_${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        downloaded++;
      } catch {
        // Counted below; the error can carry the stored URL, so it is not logged.
      }

      // Add small delay between downloads to avoid browser blocking
      if (i < allPhotos.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (downloaded === allPhotos.length) {
      toast.success(`Downloaded ${downloaded} photo(s)`);
    } else if (downloaded > 0) {
      toast.error(`Downloaded ${downloaded} of ${allPhotos.length} photos; ${allPhotos.length - downloaded} failed`);
    } else {
      toast.error('Failed to download photos');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading trucks...</div>
      </div>
    );
  }

  // Filter trucks by search query - show all trucks
  const filteredTrucks = trucks.filter(truck => {
    const query = searchQuery.toLowerCase();
    if (!query) return true; // Show all if no search query
    
    return (
      truck.unit_number?.toLowerCase().includes(query) ||
      truck.vin?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Camera className="w-6 h-6" />
              Truck Photos
            </h1>
            <p className="text-slate-600 mt-1">{canEdit ? 'View and manage truck photos' : 'View and download truck photos'}</p>
          </div>
          {canEdit && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Truck
            </button>
          )}
        </div>
        
        {/* Search Bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by unit number or VIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Unit Number</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Photos</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">VIN</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTrucks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="w-12 h-12 text-slate-300" />
                      <p className="text-slate-500 font-medium">
                        {searchQuery ? 'No trucks match your search' : 'No trucks found'}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTrucks.map((truck) => {
                  const allPhotos = getAllPhotosForTruck(truck.photos);
                  return (
                    <tr key={truck.id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-slate-900">{truck.unit_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        {allPhotos.length > 0 ? (
                          <button
                            onClick={() => openPhotoModal(allPhotos, truck)}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                          >
                            <Camera className="w-4 h-4" />
                            <span className="text-sm font-semibold">{allPhotos.length} photo{allPhotos.length !== 1 ? 's' : ''}</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Camera className="w-4 h-4" />
                            <span className="text-sm">No photos</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 font-mono">{truck.vin || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedTruckId(truck.id);
                                  setShowUploadModal(true);
                                }}
                                className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all hover:shadow-sm"
                                title="Add more photos"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAllTruckPhotos(truck.photos)}
                                className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all hover:shadow-sm"
                                title="Delete all photos"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDownloadPhotos(truck.photos)}
                            className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all hover:shadow-sm"
                            title="Download all photos"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && selectedPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              {canEdit && (
                <button
                  onClick={() => handleDeleteSinglePhoto(selectedPhotos[currentPhotoIndex])}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  title="Delete this photo"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete
                </button>
              )}
              <button
                onClick={() => setShowPhotoModal(false)}
                className="p-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="relative min-h-[400px] flex items-center justify-center">
              {(signedPhotosLoading || (imageLoading && signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()))) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              )}
              {!signedPhotosLoading && !signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()) && (
                <div className="text-white text-sm">This photo could not be loaded</div>
              )}
              {!signedPhotosLoading && signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()) && (
                <img
                  key={currentPhotoIndex}
                  src={signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim())}
                  alt={`Photo ${currentPhotoIndex + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                  style={{ display: imageLoading ? 'none' : 'block' }}
                />
              )}
            </div>
            
            {selectedPhotos.length > 1 && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : selectedPhotos.length - 1))}
                  className="px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100"
                >
                  Previous
                </button>
                <span className="text-white">
                  {currentPhotoIndex + 1} / {selectedPhotos.length}
                </span>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev < selectedPhotos.length - 1 ? prev + 1 : 0))}
                  className="px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Upload Truck Photos</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedTruckId('');
                  setSelectedFiles([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Truck Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Truck *
                </label>
                
                {/* Search Input with Autocomplete */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <input
                    type="text"
                    value={truckSearchQuery}
                    onChange={(e) => {
                      setTruckSearchQuery(e.target.value);
                      setShowTruckDropdown(true);
                      if (!e.target.value) {
                        setSelectedTruckId('');
                      }
                    }}
                    onFocus={() => setShowTruckDropdown(true)}
                    placeholder="Search by unit number or VIN..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {/* Dropdown Results */}
                  {showTruckDropdown && truckSearchQuery && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {trucks
                        .filter(truck => {
                          const query = truckSearchQuery.toLowerCase();
                          return truck.unit_number?.toLowerCase().includes(query) ||
                            truck.vin?.toLowerCase().includes(query);
                        })
                        .slice(0, 10)
                        .map((truck) => (
                          <button
                            key={truck.id}
                            type="button"
                            onClick={() => {
                              setSelectedTruckId(truck.id);
                              setTruckSearchQuery(truck.unit_number);
                              setShowTruckDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                          >
                            <div className="font-medium text-slate-900">{truck.unit_number}</div>
                            {truck.vin && (
                              <div className="text-xs text-slate-600 font-mono">{truck.vin}</div>
                            )}
                          </button>
                        ))}
                      {trucks.filter(truck => {
                        const query = truckSearchQuery.toLowerCase();
                        return truck.unit_number?.toLowerCase().includes(query) ||
                          truck.vin?.toLowerCase().includes(query);
                      }).length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">
                          No trucks found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedTruckId && (
                  <p className="mt-2 text-sm text-slate-600">
                    VIN: {trucks.find(t => t.id === selectedTruckId)?.vin || 'N/A'}
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Photos *
                </label>
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 hover:border-blue-500 transition-colors">
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-600">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to select photos</span>
                      <span className="text-xs text-slate-500">or drag and drop</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">
                    {selectedFiles.length} file(s) selected
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <p className="text-xs text-slate-600 mt-1 truncate">{file.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedTruckId('');
                    setSelectedFiles([]);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadPhotos}
                  disabled={uploading || !selectedTruckId || selectedFiles.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Photos'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
